/** Strip punctuation, lowercase, collapse whitespace */
const normalise = (s) => s
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ');
/**
 * Returns true if `label` starts with `prefix` as a whole-word boundary match.
 *
 * @example
 *   matchesPrefix('sign in to account', 'sign in') // → true
 *   matchesPrefix('sign up for emails', 'sign in') // → false
 *   matchesPrefix('sign in', 'sign in')            // → true (exact)
 */
const matchesPrefix = (label, prefix) => {
    const p = normalise(prefix);
    const l = normalise(label);
    return l === p || l.startsWith(p + ' ');
};
// ─── Rule Lists (sourced from Figma CTA Audit — EXAMPLES section) ─────────────
/**
 * Labels starting with these words → PRIMARY (Yellow, #FFD814).
 * Rule: The single most important conversion action on the page.
 * Only ONE primary button is allowed per page/view.
 */
export const PRIMARY_PREFIXES = [
    'continue', // "Continue to payment", "Continue to checkout"
    'next', // "Next", "Next step"
    'proceed', // "Proceed to checkout"
    'buy', // "Buy now", "Buy"
    'add to basket', // "Add to basket"
];
/**
 * Flying Flowers brand-specific primary overrides.
 *
 * These labels map to PRIMARY for FF but remain TERTIARY for Interflora.
 * "Add to basket" is FF's primary conversion action on product pages.
 */
export const FF_PRIMARY_OVERRIDES = [
    'add to basket', // FF product page — primary conversion CTA
];
/**
 * These exact labels would match a PRIMARY_PREFIX above but must map to
 * SECONDARY instead. Design spec exception: "Continue shopping" is a
 * navigational action, not a conversion CTA.
 */
export const PRIMARY_EXCEPTIONS = ['continue shopping', 'continue to shop'];
/**
 * Labels starting with these words → SECONDARY (Black, #000000).
 * Rule: Important supporting actions, but not the primary conversion CTA.
 *
 * Note: 'sign in' is listed in full (not just 'sign') to avoid matching
 * 'sign up' which is TERTIARY per design spec.
 */
export const SECONDARY_PREFIXES = [
    'save', // "Save changes", "Save address", "Save occasion"
    'set', // "Set reminder"
    'send', // "Send message"
    'submit', // "Submit order", "Submit details"
    'sign in', // "Sign in"
    'create', // "Create account", "Create reminder"
    'yes', // "Yes, remove it", "Yes, cancel"
    'continue shopping', // explicit secondary override (also in PRIMARY_EXCEPTIONS)
    'continue to shop',
    'view gifts', // PCC: "View gifts" (AvailabilityChecker)
    'view suitable gifts', // PCC: "View suitable gifts" (Flying Flowers)
    'view available gifts', // PCC: "View available gifts"
];
/**
 * TERTIARY (White outlined, #FFFFFF / #828282 border) — the DEFAULT.
 *
 * Anything not matched by primary or secondary rules falls here.
 * Includes (but not limited to): add, view (non-PCC), cancel, find,
 * edit, apply, sign up, no, keep shopping, page content components.
 *
 * Listed here for documentation — no runtime check needed as tertiary
 * is the catch-all return value.
 */
export const TERTIARY_EXAMPLES = [
    'add', // "Add to order", "Add"
    'view', // "View all" — generic "view" remains tertiary; PCC "view…gifts" variants → secondary (see SECONDARY_PREFIXES)
    'cancel', // "Cancel order"
    'find', // "Find your order"
    'edit', // "Edit address"
    'apply', // "Apply discount"
    'sign up', // "Sign up for emails" — NOT "sign in" (secondary)
    'no', // "No, keep it"
    'keep shopping', // "Keep shopping" (basket)
    'load more', // "Load more"
    'close', // "Close"
];
// ─── Public API ───────────────────────────────────────────────────────────────
/**
 * Derives the appropriate `ButtonVariant` from a CTA label text.
 *
 * Priority order:
 *  1. `PRIMARY_EXCEPTIONS`   → 'secondary'   (overrides any primary prefix match)
 *  2. `FF_PRIMARY_OVERRIDES` → 'primary'     (FF only — e.g. "Add to basket")
 *  3. `PRIMARY_PREFIXES`     → 'primary'
 *  4. `SECONDARY_PREFIXES`   → 'secondary'
 *  5. Default                → 'tertiary'
 *
 * Matching is case-insensitive and punctuation-insensitive. Prefix matching
 * uses whole-word boundaries to prevent false positives (e.g. "sign up"
 * does NOT match the "sign in" secondary prefix).
 *
 * @param label          - The visible button text (e.g. "Continue to payment")
 * @param isFlyingFlowers - Pass `true` for Flying Flowers brand to enable FF-specific overrides
 * @returns ButtonVariant
 *
 * @example
 *   getButtonVariant('Continue to payment')         // → 'primary'
 *   getButtonVariant('Continue shopping')           // → 'secondary'
 *   getButtonVariant('Submit order')                // → 'secondary'
 *   getButtonVariant('Sign up for emails')          // → 'tertiary'
 *   getButtonVariant('Sign in')                     // → 'secondary'
 *   getButtonVariant('Cancel')                      // → 'tertiary'
 *   getButtonVariant('Add to basket')               // → 'tertiary'  (IF)
 *   getButtonVariant('Add to basket', true)         // → 'primary'   (FF)
 *   getButtonVariant('')                            // → 'tertiary'
 */
export function getButtonVariant(label, isFlyingFlowers) {
    if (!(label === null || label === void 0 ? void 0 : label.trim()))
        return 'tertiary';
    // 1. Primary exceptions — must be checked before primary prefix list
    if (PRIMARY_EXCEPTIONS.some((exc) => matchesPrefix(label, exc))) {
        return 'secondary';
    }
    // 2. FF brand-specific primary overrides (e.g. "Add to basket")
    if (isFlyingFlowers && FF_PRIMARY_OVERRIDES.some((p) => matchesPrefix(label, p))) {
        return 'primary';
    }
    // 3. Primary prefix match
    if (PRIMARY_PREFIXES.some((p) => matchesPrefix(label, p))) {
        return 'primary';
    }
    // 4. Secondary prefix match
    if (SECONDARY_PREFIXES.some((p) => matchesPrefix(label, p))) {
        return 'secondary';
    }
    // 5. Default
    return 'tertiary';
}
